import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Palette, Apps, Image, AttachFile, Delete, Language } from "@material-ui/icons";
import { TextField, Box, Grid, Select, MenuItem, InputLabel, FormControl } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import ColorBoxModal from "../../components/ColorBoxModal";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import { getBackendUrl } from "../../config";
import defaultLogoLight from "../../assets/logo.png";
import defaultLogoDark from "../../assets/logo-black.png";
import defaultLogoFavicon from "../../assets/favicon.ico";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
    maxHeight: "100vh",
    overflowY: "auto",
  },
  header: {
    marginBottom: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },
  sectionCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "16px",
    borderBottom: "1px solid #e8ecf1",
  },
  sectionIcon: {
    marginRight: "12px",
    color: "#3b82f6",
    fontSize: "24px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#1a1a1a",
  },
  uploadCard: {
    border: "2px dashed #e8ecf1",
    borderRadius: "8px",
    padding: "24px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: "#fafbfc",
    "&:hover": {
      borderColor: "#3b82f6",
      background: "white",
    },
  },
  uploadCardActive: {
    borderColor: "#3b82f6",
    background: "white",
  },
  uploadIcon: {
    fontSize: "40px",
    color: "#3b82f6",
    marginBottom: "8px",
  },
  uploadText: {
    fontSize: "13px",
    color: "#666",
    fontWeight: 500,
  },
  previewContainer: {
    position: "relative",
    marginTop: "12px",
    padding: "12px",
    background: "#f8f9fa",
    borderRadius: "6px",
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "100px",
    objectFit: "contain",
    borderRadius: "4px",
  },
  deleteButton: {
    position: "absolute",
    top: "4px",
    right: "4px",
    background: "#fff",
    padding: "4px 8px",
    borderRadius: "4px",
    border: "1px solid #e8ecf1",
    cursor: "pointer",
    fontSize: "18px",
    color: "#666",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      background: "#ff4444",
      borderColor: "#ff4444",
      color: "#fff",
    },
  },
  uploadInput: {
    display: "none",
  },
  colorBox: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    background: "#fafbfc",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#3b82f6",
      background: "white",
    },
  },
  colorPreview: {
    width: "32px",
    height: "32px",
    borderRadius: "6px",
    border: "2px solid #fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  colorValue: {
    fontSize: "13px",
    color: "#666",
    fontWeight: 500,
  },
}));

export default function Whitelabel() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  
  const [appName, setAppName] = useState("");
  const [primaryColorLight, setPrimaryColorLight] = useState("#1976d2");
  const [primaryColorDark, setPrimaryColorDark] = useState("#1976d2");
  const [listItemBgColor, setListItemBgColor] = useState("#ffffff");
  
  const [appLogoLight, setAppLogoLight] = useState("");
  const [appLogoDark, setAppLogoDark] = useState("");
  const [appLogoFavicon, setAppLogoFavicon] = useState("");
  const [appLogoBackgroundLight, setAppLogoBackgroundLight] = useState("");
  const [appLogoBackgroundDark, setAppLogoBackgroundDark] = useState("");
  
  const logoLightInput = useRef(null);
  const logoDarkInput = useRef(null);
  const logoFaviconInput = useRef(null);
  const backgroundLightInput = useRef(null);
  const backgroundDarkInput = useRef(null);
  
  const [colorLightOpen, setColorLightOpen] = useState(false);
  const [colorDarkOpen, setColorDarkOpen] = useState(false);
  const [colorBgOpen, setColorBgOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(() => i18n.language || localStorage.getItem("i18nextLng") || "pt");

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem(`language_${user.id}`, lang);
    localStorage.setItem("i18nextLng", lang);
    toast.success("✅ Idioma alterado!");
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        
        const appNameSetting = data.find((s) => s.key === "appName");
        const colorLightSetting = data.find((s) => s.key === "primaryColorLight");
        const colorDarkSetting = data.find((s) => s.key === "primaryColorDark");
        const colorBgSetting = data.find((s) => s.key === "listItemBgColor");
        const logoLightSetting = data.find((s) => s.key === "appLogoLight");
        const logoDarkSetting = data.find((s) => s.key === "appLogoDark");
        const logoFaviconSetting = data.find((s) => s.key === "appLogoFavicon");
        const bgLightSetting = data.find((s) => s.key === "appLogoBackgroundLight");
        const bgDarkSetting = data.find((s) => s.key === "appLogoBackgroundDark");

        setAppName(appNameSetting?.value || "");
        setPrimaryColorLight(colorLightSetting?.value || "#1976d2");
        setPrimaryColorDark(colorDarkSetting?.value || "#1976d2");
        setListItemBgColor(colorBgSetting?.value || "#ffffff");
        setAppLogoLight(logoLightSetting?.value || "");
        setAppLogoDark(logoDarkSetting?.value || "");
        setAppLogoFavicon(logoFaviconSetting?.value || "");
        setAppLogoBackgroundLight(bgLightSetting?.value || "");
        setAppLogoBackgroundDark(bgDarkSetting?.value || "");
      } catch (error) {
        console.error("Erro ao carregar:", error);
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      toast.success("✅ Salvo!");
      return true;
    } catch (error) {
      console.error("Erro:", error);
      toast.error("❌ Erro: " + (error.response?.data?.error || error.message));
      return false;
    }
  };

  const handleSaveAppName = async () => {
    if (!appName.trim()) {
      toast.error("Nome não pode estar vazio");
      return;
    }
    const success = await saveSetting("appName", appName);
    if (success) colorMode.setAppName(appName);
  };

  const handleColorChange = async (key, color, setter, colorModeFn) => {
    let hexColor = color;
    if (typeof color === "object" && color.hex) {
      hexColor = `#${color.hex}`;
    } else if (typeof color === "string" && !color.startsWith("#")) {
      hexColor = `#${color}`;
    }

    setter(hexColor);
    const success = await saveSetting(key, hexColor);
    if (success && colorModeFn) colorModeFn(hexColor);
  };

  const uploadLogo = async (e, mode) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("typeArch", "logo");
    formData.append("mode", mode);
    formData.append("file", file);

    try {
      const { data } = await api.post("/settings-whitelabel/logo", formData);
      
      if (mode === "Light") {
        setAppLogoLight(data);
        colorMode.setAppLogoLight(getBackendUrl() + "/public/" + data);
      } else if (mode === "Dark") {
        setAppLogoDark(data);
        colorMode.setAppLogoDark(getBackendUrl() + "/public/" + data);
      } else if (mode === "Favicon") {
        setAppLogoFavicon(data);
        colorMode.setAppLogoFavicon(getBackendUrl() + "/public/" + data);
      } else if (mode === "BackgroundLight") {
        setAppLogoBackgroundLight(data);
      } else if (mode === "BackgroundDark") {
        setAppLogoBackgroundDark(data);
      }
      
      toast.success("✅ Logo enviado!");
    } catch (error) {
      console.error("Erro ao enviar logo:", error);
      toast.error("❌ Erro ao enviar logo");
    }
  };

  const deleteLogo = async (mode) => {
    try {
      await saveSetting(`appLogo${mode}`, "");
      
      if (mode === "Light") {
        setAppLogoLight("");
        colorMode.setAppLogoLight(defaultLogoLight);
      } else if (mode === "Dark") {
        setAppLogoDark("");
        colorMode.setAppLogoDark(defaultLogoDark);
      } else if (mode === "Favicon") {
        setAppLogoFavicon("");
        colorMode.setAppLogoFavicon(defaultLogoFavicon);
      } else if (mode === "BackgroundLight") {
        setAppLogoBackgroundLight("");
      } else if (mode === "BackgroundDark") {
        setAppLogoBackgroundDark("");
      }
      
      toast.success("✅ Logo removido!");
    } catch (error) {
      toast.error("❌ Erro ao remover logo");
    }
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  if (loading) {
    return (
      <MainContainer>
        <div style={{ padding: "50px", textAlign: "center" }}>Carregando...</div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className={classes.container}>
        {/* Header */}
        <div className={classes.header}>
          <h1 className={classes.title}>🎨 Personalização do Sistema</h1>
        </div>

        {/* Configurações Gerais */}
        <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <Apps className={classes.sectionIcon} />
            <span className={classes.sectionTitle}>Informações Gerais</span>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome do Sistema"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                onBlur={handleSaveAppName}
                variant="outlined"
                size="small"
                placeholder="Ex: Minha Empresa"
                helperText="Pressione Enter ou clique fora para salvar"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel><Language style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }} />Idioma</InputLabel>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  label="Idioma"
                >
                  <MenuItem value="pt">🇧🇷 Português</MenuItem>
                  <MenuItem value="en">🇺🇸 English</MenuItem>
                  <MenuItem value="es">🇪🇸 Español</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>

        {/* Cores */}
        <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <Palette className={classes.sectionIcon} />
            <span className={classes.sectionTitle}>Paleta de Cores</span>
          </div>

          <Grid container spacing={2}>
            {/* Cor Primária Tema Claro */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Cor Primária (Tema Claro)
                </label>
              </div>
              <div className={classes.colorBox} onClick={() => setColorLightOpen(true)}>
                <div
                  className={classes.colorPreview}
                  style={{ backgroundColor: primaryColorLight }}
                />
                <span className={classes.colorValue}>{primaryColorLight}</span>
              </div>
              <ColorBoxModal
                open={colorLightOpen}
                handleClose={() => setColorLightOpen(false)}
                onChange={(color) => {
                  handleColorChange("primaryColorLight", color, setPrimaryColorLight, colorMode.setPrimaryColorLight);
                  setColorLightOpen(false);
                }}
                currentColor={primaryColorLight}
              />
            </Grid>

            {/* Cor Primária Tema Escuro */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Cor Primária (Tema Escuro)
                </label>
              </div>
              <div className={classes.colorBox} onClick={() => setColorDarkOpen(true)}>
                <div
                  className={classes.colorPreview}
                  style={{ backgroundColor: primaryColorDark }}
                />
                <span className={classes.colorValue}>{primaryColorDark}</span>
              </div>
              <ColorBoxModal
                open={colorDarkOpen}
                handleClose={() => setColorDarkOpen(false)}
                onChange={(color) => {
                  handleColorChange("primaryColorDark", color, setPrimaryColorDark, colorMode.setPrimaryColorDark);
                  setColorDarkOpen(false);
                }}
                currentColor={primaryColorDark}
              />
            </Grid>

            {/* Cor de Fundo dos Itens */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Cor de Fundo dos Itens
                </label>
              </div>
              <div className={classes.colorBox} onClick={() => setColorBgOpen(true)}>
                <div
                  className={classes.colorPreview}
                  style={{ backgroundColor: listItemBgColor }}
                />
                <span className={classes.colorValue}>{listItemBgColor}</span>
              </div>
              <ColorBoxModal
                open={colorBgOpen}
                handleClose={() => setColorBgOpen(false)}
                onChange={(color) => {
                  handleColorChange("listItemBgColor", color, setListItemBgColor, colorMode.setListItemBgColor);
                  setColorBgOpen(false);
                }}
                currentColor={listItemBgColor}
              />
            </Grid>
          </Grid>
        </div>

        {/* Logos e Imagens */}
        <div className={classes.sectionCard}>
          <div className={classes.sectionHeader}>
            <Image className={classes.sectionIcon} />
            <span className={classes.sectionTitle}>Logos e Imagens</span>
          </div>

          <Grid container spacing={2}>
            {/* Logo Tema Claro */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Logo (Tema Claro)
                </label>
              </div>
              <div
                className={`${classes.uploadCard} ${appLogoLight ? classes.uploadCardActive : ""}`}
                onClick={() => !appLogoLight && logoLightInput.current.click()}
              >
                {!appLogoLight ? (
                  <>
                    <AttachFile className={classes.uploadIcon} />
                    <div className={classes.uploadText}>Clique para fazer upload</div>
                  </>
                ) : (
                  <div className={classes.previewContainer}>
                    <div className={classes.deleteButton} onClick={(e) => { e.stopPropagation(); deleteLogo("Light"); }}>
                      <Delete style={{ fontSize: "16px" }} />
                    </div>
                    <img
                      src={getBackendUrl() + "/public/" + appLogoLight}
                      alt="Logo Light"
                      className={classes.previewImage}
                      onError={(e) => { e.target.src = defaultLogoLight; }}
                    />
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={logoLightInput}
                className={classes.uploadInput}
                accept="image/*"
                onChange={(e) => uploadLogo(e, "Light")}
              />
            </Grid>

            {/* Logo Tema Escuro */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Logo (Tema Escuro)
                </label>
              </div>
              <div
                className={`${classes.uploadCard} ${appLogoDark ? classes.uploadCardActive : ""}`}
                onClick={() => !appLogoDark && logoDarkInput.current.click()}
              >
                {!appLogoDark ? (
                  <>
                    <AttachFile className={classes.uploadIcon} />
                    <div className={classes.uploadText}>Clique para fazer upload</div>
                  </>
                ) : (
                  <div className={classes.previewContainer}>
                    <div className={classes.deleteButton} onClick={(e) => { e.stopPropagation(); deleteLogo("Dark"); }}>
                      <Delete style={{ fontSize: "16px" }} />
                    </div>
                    <img
                      src={getBackendUrl() + "/public/" + appLogoDark}
                      alt="Logo Dark"
                      className={classes.previewImage}
                      onError={(e) => { e.target.src = defaultLogoDark; }}
                    />
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={logoDarkInput}
                className={classes.uploadInput}
                accept="image/*"
                onChange={(e) => uploadLogo(e, "Dark")}
              />
            </Grid>

            {/* Favicon */}
            <Grid item xs={12} sm={6} md={4}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Favicon
                </label>
              </div>
              <div
                className={`${classes.uploadCard} ${appLogoFavicon ? classes.uploadCardActive : ""}`}
                onClick={() => !appLogoFavicon && logoFaviconInput.current.click()}
              >
                {!appLogoFavicon ? (
                  <>
                    <AttachFile className={classes.uploadIcon} />
                    <div className={classes.uploadText}>Clique para fazer upload</div>
                  </>
                ) : (
                  <div className={classes.previewContainer}>
                    <div className={classes.deleteButton} onClick={(e) => { e.stopPropagation(); deleteLogo("Favicon"); }}>
                      <Delete style={{ fontSize: "16px" }} />
                    </div>
                    <img
                      src={getBackendUrl() + "/public/" + appLogoFavicon}
                      alt="Favicon"
                      className={classes.previewImage}
                      onError={(e) => { e.target.src = defaultLogoFavicon; }}
                    />
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={logoFaviconInput}
                className={classes.uploadInput}
                accept="image/*"
                onChange={(e) => uploadLogo(e, "Favicon")}
              />
            </Grid>

            {/* Background Tema Claro */}
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Fundo Menu (Tema Claro)
                </label>
              </div>
              <div
                className={`${classes.uploadCard} ${appLogoBackgroundLight ? classes.uploadCardActive : ""}`}
                onClick={() => !appLogoBackgroundLight && backgroundLightInput.current.click()}
              >
                {!appLogoBackgroundLight ? (
                  <>
                    <AttachFile className={classes.uploadIcon} />
                    <div className={classes.uploadText}>Clique para fazer upload</div>
                  </>
                ) : (
                  <div className={classes.previewContainer}>
                    <div className={classes.deleteButton} onClick={(e) => { e.stopPropagation(); deleteLogo("BackgroundLight"); }}>
                      <Delete style={{ fontSize: "16px" }} />
                    </div>
                    <img
                      src={getBackendUrl() + "/public/" + appLogoBackgroundLight}
                      alt="Background Light"
                      className={classes.previewImage}
                    />
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={backgroundLightInput}
                className={classes.uploadInput}
                accept="image/*"
                onChange={(e) => uploadLogo(e, "BackgroundLight")}
              />
            </Grid>

            {/* Background Tema Escuro */}
            <Grid item xs={12} md={6}>
              <div style={{ marginBottom: "8px" }}>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#666" }}>
                  Fundo Menu (Tema Escuro)
                </label>
              </div>
              <div
                className={`${classes.uploadCard} ${appLogoBackgroundDark ? classes.uploadCardActive : ""}`}
                onClick={() => !appLogoBackgroundDark && backgroundDarkInput.current.click()}
              >
                {!appLogoBackgroundDark ? (
                  <>
                    <AttachFile className={classes.uploadIcon} />
                    <div className={classes.uploadText}>Clique para fazer upload</div>
                  </>
                ) : (
                  <div className={classes.previewContainer}>
                    <div className={classes.deleteButton} onClick={(e) => { e.stopPropagation(); deleteLogo("BackgroundDark"); }}>
                      <Delete style={{ fontSize: "16px" }} />
                    </div>
                    <img
                      src={getBackendUrl() + "/public/" + appLogoBackgroundDark}
                      alt="Background Dark"
                      className={classes.previewImage}
                    />
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={backgroundDarkInput}
                className={classes.uploadInput}
                accept="image/*"
                onChange={(e) => uploadLogo(e, "BackgroundDark")}
              />
            </Grid>
          </Grid>
        </div>
      </div>
    </MainContainer>
  );
}
